import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Button
} from 'reactstrap';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import { FaTimes } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import './ExamList.css';
import AllExam from './AllExam';
import ResultTab from './AllResult';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import PromoteStudents from './PromotedStudents';
import Footer from 'components/Footers/Footer';

const baseURL = apiURL1;

function ExamList() {
  const token = localStorage.getItem("token");

  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [classId, setClassId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [type, setType] = useState('');
  const [year, setYear] = useState('');
  const [classes, setClasses] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  const [courses, setCourses] = useState([]);
  const currentYear = dayjs().year();
  const [activeTab, setActiveTab] = useState('exam');

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchExams();
  }, [page, classId, courseId, type, year]);

  useEffect(() => {
    const fetchCourses = async () => {
      if (classId) {
        try {
          const response = await axios.get(`${baseURL}/superadmin/oneClassForExam/${classId}`, {
            headers: {
              'Authorization': `Bearer ${token}`, // Include the token in the headers
            }
          });
          const classData = response.data.data;
          if (classData) {
            setCourses([classData.course]);
            setCourseId(classData.course._id);
          } else {
            setCourses([]);
            setCourseId('');
          }
        } catch (error) {
          console.error('Error fetching courses:', error);
          toast.error('Error fetching courses');
          setCourses([]);
          setCourseId('');
        }
      } else {
        setCourses([]);
        setCourseId('');
      }
    };

    fetchCourses();
  }, [classId]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${baseURL}/superadmin/allClassForExam`, {
        headers: {
          'Authorization': `Bearer ${token}`, // Include the token in the headers
        }
      });
      setClasses(response.data.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error('Error fetching classes');
    }
  };

  const fetchExams = async () => {
    setLoading(true);
    try {
      let url = `${baseURL}/superadmin/filterExams?page=${page}`;
      if (classId) url += `&classId=${classId}`;
      if (courseId) url += `&courseId=${courseId}`;
      if (type) url += `&type=${type ? type._id : ''}`;
      if (year) url += `&year=${year}`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`, // Include the token in the headers
        }
      });
      if (response.data && response.data.data) {
        setExams(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setExams([]);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
      toast.error('Error fetching exams');
      setExams([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (e) => {
    setClassId(e.target.value);
    setCourseId('');
  };

  const handleCourseChange = (e) => {
    setCourseId(e.target.value);
  };

  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  const handleYearChange = (e) => {
    setYear(e.target.value);
  };

  const openDeleteModal = (examId) => {
    setExamToDelete(examId);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setExamToDelete(null);
    setDeleteModal(false);
  };

  const handleDeleteExam = async () => {
    setLoading(true);
    try {
      await axios.delete(`${baseURL}/superadmin/exams/${examToDelete}`, {
        headers: {
          'Authorization': `Bearer ${token}`, // Include the token in the headers
        }
      });
      toast.success('Exam deleted successfully!');
      fetchExams();
    } catch (error) {
      console.error('Error deleting exam:', error);
      toast.error('Error deleting exam');
    } finally {
      setLoading(false);
      closeDeleteModal();
    }
  };

  const generateExamReport = async () => {
    setLoading(true);
    try {
      let url = `${baseURL}/superadmin/filterExams`;
      if (classId) url += `?classId=${classId}`;
      if (courseId) url += `&courseId=${courseId}`;
      if (type) url += `&type=${type ? type._id : ''}`;
      if (year) url += `&year=${year}`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`, // Include the token in the headers
        }
      });
      const allExams = response.data.data;

      const pdf = new jsPDF();
      pdf.setFontSize(18);
      pdf.text('Exam List Report', 10, 10);

      const tableData = allExams.map(exam => [
        exam.class.className,
        exam.course.courseName,
        exam.type,
        exam.year,
        exam.status === 'draft' ? 'DRAFT' : 'PUBLISHED'
      ]);

      pdf.autoTable({
        head: [['Class', 'Course', 'Type', 'Year', 'Status']],
        body: tableData,
        startY: 20,
        styles: {
          fontSize: 10,
          cellPadding: 5,
          overflow: 'linebreak',
          halign: 'center',
          valign: 'middle',
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center',
        },
        alternateRowStyles: {
          fillColor: [220, 220, 220]
        }
      });

      pdf.save('exam-list-report.pdf');
    } catch (error) {
      console.error('Error generating exam report:', error);
      toast.error('Error generating exam report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const renderPagination = () => {
    const paginationItems = [];
    for (let i = 1; i <= totalPages; i++) {
      paginationItems.push(
        <PaginationItem key={i} active={i === page}>
          <PaginationLink onClick={() => setPage(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination style={{ justifyContent: 'center' }}>
        <PaginationItem disabled={page === 1}>
          <PaginationLink previous onClick={() => setPage(page - 1)} />
        </PaginationItem>
        {paginationItems}
        <PaginationItem disabled={page === totalPages}>
          <PaginationLink next onClick={() => setPage(page + 1)} />
        </PaginationItem>
      </Pagination>
    );
  };

  return (
    <>

      <div
        className="header d-flex align-items-center"
        style={{
          minHeight: "400px",
          backgroundImage:
            "url(" + require("../../assets/img/theme/alRehman.jpg") + ")",
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      >
        {/* Mask */}
        <span className="mask bg-gradient-default opacity-8" />
        {/* Header container */}
        <Container className="d-flex" fluid>
          <Row>
            <Col lg="7" md="10">
              <h1 className="display-2 text-white">Academic Progress Management</h1>
              <p className="text-white mt-0 mb-5">
                Here you can view and manage the academic progress details.
                Review the information carefully and make any
                necessary updates or follow-ups.
              </p>
            </Col>
          </Row>
        </Container>
      </div>
      <Container className="mt--5" fluid>
        <Row>
          <Col>
            <div className="d-flex justify-content-center">
              <Button
                color={
                  activeTab === "exam" ? "danger" : "secondary"
                }
                onClick={() => setActiveTab("exam")}
                style={{ marginRight: '10px' }}
              >
                Exam
              </Button>
              <Button
                color={
                  activeTab === "result" ? "warning" : "secondary"
                }
                onClick={() => setActiveTab("result")}
                style={{ marginRight: '10px' }}
              >
                Result
              </Button>
              <Button
                color={activeTab === "promotion" ? "info" : "secondary"}
                onClick={() => setActiveTab("promotion")}
              >
                Promotions
              </Button>
            </div>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            {activeTab === "exam" && (
              <AllExam
                exams={exams}
                setExams={setExams}
                loading={loading}
                setLoading={setLoading}
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                setTotalPages={setTotalPages}
                classId={classId}
                setClassId={setClassId}
                courseId={courseId}
                setCourseId={setCourseId}
                type={type}
                setType={setType}
                year={year}
                setYear={setYear}
                classes={classes}
                setClasses={setClasses}
                deleteModal={deleteModal}
                setDeleteModal={setDeleteModal}
                examToDelete={examToDelete}
                setExamToDelete={setExamToDelete}
                courses={courses}
                setCourses={setCourses}
                fetchClasses={fetchClasses}
                fetchExams={fetchExams}
                handleClassChange={handleClassChange}
                handleCourseChange={handleCourseChange}
                handleTypeChange={handleTypeChange}
                handleYearChange={handleYearChange}
                openDeleteModal={openDeleteModal}
                closeDeleteModal={closeDeleteModal}
                handleDeleteExam={handleDeleteExam}
                generateExamReport={generateExamReport}
                handlePrint={handlePrint}
                renderPagination={renderPagination}
              />
            )}
            {activeTab === "result" && (
              <ResultTab
                exams={exams}
                loading={loading}
                renderPagination={renderPagination}
              />
            )}
            {activeTab === "promotion" && (
              <PromoteStudents />
            )}
          </Col>
        </Row>

        {/* Delete Exam Modal */}
        <Modal isOpen={deleteModal} toggle={closeDeleteModal}>
          <ModalHeader toggle={closeDeleteModal}>
            Delete Exam
            <Button
              className="close"
              onClick={closeDeleteModal}
              style={{ position: 'absolute', top: '10px', right: '10px' }}
            >
              <FaTimes />
            </Button>
          </ModalHeader>
          <ModalBody>
            <p>Are you sure you want to delete this exam?</p>
            <Button color="danger" onClick={handleDeleteExam}>
              Yes, Delete
            </Button>
            <Button color="secondary" onClick={closeDeleteModal}>
              Cancel
            </Button>
          </ModalBody>
        </Modal>

        <Footer />
      </Container>
    </>
  );
}

export default ExamList;
